import type {
  ReconcileResult,
  RevolutMerchantImportResult,
  BraintreeReconcileResult,
  BraintreeUnmatchedPayment,
  PaypalReconcileResult,
  VascoReconcileResult,
  PaypalSettlementCheck,
  LinkedToOtherPayments,
  PaymentDiscrepancy,
  MatchResult,
} from '../components/accounting/types';

/**
 * The text shown after an import. `warning` is true when the import finished
 * but left something the user has to act on: the message is then shown orange,
 * not green. Green means there is nothing to do.
 *
 * Layout of every message: the first line holds the counts of what worked,
 * then each problem follows as one sentence on its own line.
 */
export interface ResultMessage {
  message: string;
  warning: boolean;
}

export function buildResultMessage(summary: string, problems: string[], notes: string[] = []): ResultMessage {
  return { message: [summary, ...problems, ...notes].join('\n'), warning: problems.length > 0 };
}

/** "1 order", "3 orders". */
export function count(n: number, word: string, plural = `${word}s`): string {
  return `${n} ${n === 1 ? word : plural}`;
}

const itOrThem = (n: number) => (n === 1 ? 'it' : 'them');

/** YYYY-MM-DD → DD.MM.YYYY. Anything else is returned unchanged. */
export function formatIsoDate(value: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value || '');
  return m ? `${m[3]}.${m[2]}.${m[1]}` : (value || '?');
}

/** At most `max` items, then "+N more". */
function listSome(items: string[], max = 5): string {
  const shown = items.slice(0, max);
  return shown.join(', ') + (items.length > max ? `, +${items.length - max} more` : '');
}

/**
 * One sentence per new link after which the payments of an order together pay
 * more than its total. Without the list (older server), one sentence with the count.
 */
export function linkedToOtherPaymentsProblems(list: LinkedToOtherPayments[] | undefined, fallbackCount = 0): string[] {
  const items = list ?? [];
  if (!items.length) {
    return fallbackCount > 0
      ? [`${count(fallbackCount, 'order')} now paid more than ${fallbackCount === 1 ? 'its' : 'their'} total. Check for a double charge or a wrong link.`]
      : [];
  }
  const lines = items.slice(0, 5).map((l) => {
    const cur = l.currency ? ` ${l.currency}` : '';
    const total = l.orderTotal != null ? ` (${l.orderTotal.toFixed(2)}${cur})` : '';
    const over = l.overpaidBy ? `${l.overpaidBy.toFixed(2)}${cur} ` : '';
    return `Order ${l.orderNumber ? `#${l.orderNumber}` : '(unknown number)'}${total} is now paid by payment #${l.paymentId} `
      + `and also by payment ${l.otherPaymentIds.map((id) => `#${id}`).join(', ')}. `
      + `Together they pay ${over}more than the order total. Check for a double charge or a wrong link.`;
  });
  if (items.length > 5) lines.push(`${count(items.length - 5, 'more order')} paid more than ${items.length - 5 === 1 ? 'its' : 'their'} total.`);
  return lines;
}

/** One sentence per matched payment whose recomputed status is not 'auto'. */
export function paymentDiscrepancyProblems(list: PaymentDiscrepancy[] | undefined): string[] {
  return (list ?? []).map((p) =>
    `Payment #${p.paymentId} (${Number(p.amount).toFixed(2)} ${p.currency}) was linked, but the amounts do not add up (${p.status}). `
    + 'Check for a credit note, a double payment or a partial payment.');
}

function discrepancySentence(n: number): string {
  return `The amounts do not add up on ${count(n, 'matched payment')}. Check ${itOrThem(n)} in the payments list.`;
}

// ─── Revolut card payments (CSV, overwrite, Merchant API) ─────────────────────

/**
 * An ambiguous payout means its whole slice was discarded, so the affected
 * payments show up empty. Name the candidate payments, otherwise the count
 * alone gives no way to find them again once the message is gone.
 */
function formatAmbiguousPaymentIds(ambiguousPayouts: ReconcileResult['ambiguousPayouts']): string {
  const ids = Array.from(new Set((ambiguousPayouts || []).flatMap((p) => p.candidatePaymentIds || [])));
  if (!ids.length) return '';
  return ` (payments ${listSome(ids.map(String), 10)})`;
}

/**
 * The counts of what worked in a Revolut card reconciliation. A missing count
 * is shown as 0, so a result without counts never shows "undefined".
 */
export function formatRevolutReconcileParts(data: ReconcileResult): string[] {
  const salesLinked = data.salesLinked ?? 0;
  const payoutsMatched = data.payoutsMatched ?? 0;
  const parts: string[] = [
    `${count(salesLinked, 'order')} linked`,
    `${count(payoutsMatched, 'payout')} matched`,
  ];
  if (data.salesAlreadyLinked > 0) parts.push(`${data.salesAlreadyLinked} already linked`);
  if ((data.refundsRecorded || 0) > 0) parts.push(`${count(data.refundsRecorded!, 'refund')} recorded`);
  if ((data.refundFundingTransfersRecorded || 0) > 0) parts.push(count(data.refundFundingTransfersRecorded!, 'refund-funding transfer'));
  if ((data.chainsWithCarryover || 0) > 0) parts.push(count(data.chainsWithCarryover!, 'carryover chain'));
  return parts;
}

/**
 * One sentence per problem of a Revolut card reconciliation.
 * `payoutDetails` is false for the Merchant API import: its server notes
 * already describe the unmatched payouts and the moved rows.
 */
export function revolutReconcileProblems(data: ReconcileResult, { payoutDetails = true } = {}): string[] {
  const problems: string[] = [];
  if (payoutDetails && (data.payoutsUnmatched || 0) > 0) {
    const list = (data.unmatchedPayouts ?? [])
      .map((p) => `${Number(p.amount).toFixed(2)} ${p.currency} on ${formatIsoDate(p.date)}`);
    problems.push(`${count(data.payoutsUnmatched, 'Revolut payout')} found no bank payment${list.length ? `: ${listSome(list)}` : ''}. `
      + 'Their orders were not linked. Import the bank payments first, then import this file again.');
  }
  if ((data.payoutsAmbiguous || 0) > 0) {
    problems.push(`${count(data.payoutsAmbiguous!, 'Revolut payout')} matched more than one bank payment, so their orders were not linked${formatAmbiguousPaymentIds(data.ambiguousPayouts)}.`);
  }
  if ((data.paymentsWithDiscrepancy || 0) > 0) problems.push(discrepancySentence(data.paymentsWithDiscrepancy!));
  if ((data.linksWithMultipleSettlements || 0) > 0) {
    problems.push(`${count(data.linksWithMultipleSettlements!, 'order')} paid more than once in the same payout. Check for a duplicate charge.`);
  }
  if (data.salesOrderNotFound > 0) {
    problems.push(`The order was not found for ${count(data.salesOrderNotFound, 'card payment')}. Run Sync to fetch new orders.`);
  }
  if ((data.salesNoOrderRef || 0) > 0) {
    problems.push(`${count(data.salesNoOrderRef!, 'card payment')} without an order reference. Link ${itOrThem(data.salesNoOrderRef!)} by hand.`);
  }
  if ((data.salesCurrencyMismatch || 0) > 0) {
    problems.push(`${count(data.salesCurrencyMismatch!, 'card payment')} not linked, because the order has a different currency.`);
  }
  problems.push(...linkedToOtherPaymentsProblems(data.linkedToOtherPayments, data.salesConflictingLink || 0));
  if ((data.refundsOrderNotFound || 0) > 0) {
    problems.push(`The order was not found for ${count(data.refundsOrderNotFound!, 'refund')}.`);
  }
  if ((data.orderTotalMismatches || 0) > 0) {
    problems.push(`The order total differs from the Revolut amount for ${count(data.orderTotalMismatches!, 'order')}.`);
  }
  if ((data.balanceMismatches || 0) > 0) {
    problems.push(`Revolut's running balance does not match for ${count(data.balanceMismatches!, 'payout')}.`);
  }
  if (payoutDetails) {
    for (const { paymentId, rows } of data.paymentsWithMovedRows ?? []) {
      problems.push(`Payment #${paymentId} lost ${count(rows, 'card transaction')} to another payout in this import. Check its order links.`);
    }
  }
  return problems;
}

export function formatRevolutReconcileResult(data: ReconcileResult, prefix: string): ResultMessage {
  return buildResultMessage(`${prefix}${formatRevolutReconcileParts(data).join(' · ')}`, revolutReconcileProblems(data));
}

/**
 * The Merchant API import. Its server notes describe the payouts that need
 * attention; they are shown below the counts. It is a warning when a payout
 * stopped, was skipped or waits, when nothing could be imported (from is
 * null), or when one of the shared problem counts is set.
 */
export function formatRevolutMerchantImportResult(data: RevolutMerchantImportResult, prefix = ''): ResultMessage {
  const notes = data.notes ?? [];
  const problems = revolutReconcileProblems(data, { payoutDetails: false });
  const stopped = (data.payoutsUnbalanced || 0) + (data.payoutsSkipped || 0) + (data.payoutsWaiting || 0)
    + (data.payoutsUnmatched || 0);
  const nothingImported = data.from === null && notes.length > 0;
  const result = buildResultMessage(`${prefix}${formatRevolutReconcileParts(data).join(' · ')}`, problems, notes);
  return { ...result, warning: result.warning || stopped > 0 || nothingImported };
}

/** Problem lines for the overwrite preview: what the re-import would leave behind. */
export function previewProblemBlock(problems: string[]): string {
  return problems.length ? `\n\nThe re-import would leave these problems:\n${problems.join('\n')}` : '';
}

// ─── Braintree / AIB ──────────────────────────────────────────────────────────

/**
 * One sentence per Braintree payment that found no AIB settlements adding up to
 * it, with what the accountant should do next.
 */
export function formatBraintreeUnmatchedPayment(p: BraintreeUnmatchedPayment): string {
  const label = `Payment #${p.paymentId} (${p.amount.toFixed(2)} ${p.currency}, ${formatIsoDate(p.paymentDate)})`;
  const run = p.closestRun;
  const runText = run
    ? `The closest AIB settlements (${formatIsoDate(run.settlementDateFrom)}–${formatIsoDate(run.settlementDateTo)}) add up to ${run.sum.toFixed(2)} ${p.currency}, ${Math.abs(run.shortBy).toFixed(2)} ${p.currency} ${run.shortBy > 0 ? 'short' : 'too much'}.`
    : '';
  if (p.reason === 'report_too_short') {
    const dataEnd = p.latestSettlementDate
      ? `the AIB data ends on ${formatIsoDate(p.latestSettlementDate)}`
      : `there is no AIB data in ${p.currency}`;
    return `${label} probably includes AIB settlements up to ${formatIsoDate(p.paymentDate)}, but ${dataEnd}. `
      + 'Download the AIB report again later and import it.'
      + (run && run.shortBy > 0 ? ` ${runText}` : '');
  }
  if (p.reason === 'no_settlements') {
    return `${label}: no AIB settlements found between ${formatIsoDate(p.windowStart)} and ${formatIsoDate(p.windowEnd)}. `
      + 'Import the AIB report for this period.';
  }
  return `${label}: no run of AIB settlement days adds up to this amount. ${runText} Check the AIB report for missing rows or adjustments.`;
}

export function braintreeReconcileProblems(data: BraintreeReconcileResult): string[] {
  const problems: string[] = [];
  const unmatched = data.unmatchedPayments ?? [];
  const unmatchedCount = Math.max(unmatched.length, data.paymentsUnmatched || 0);
  if (unmatchedCount > 0) {
    problems.push(`${count(unmatchedCount, 'payment')} could not be matched.`);
    problems.push(...unmatched.map(formatBraintreeUnmatchedPayment));
  }
  // Each duplicate means one CSV row overwrote another on upsert, so a real
  // settlement was lost. It must never pass unnoticed.
  const duplicates = data.duplicateTransactionIds ?? [];
  if (duplicates.length > 0) {
    problems.push(`${count(duplicates.length, 'DUPLICATE Transaction ID')} in the AIB report: ${duplicates.join(', ')}`);
  }
  if ((data.paymentsWithDiscrepancy || 0) > 0) problems.push(discrepancySentence(data.paymentsWithDiscrepancy!));
  for (const p of data.paymentsWithMissingOrders ?? []) {
    const named = p.orderNumbers.filter(Boolean).map((n) => `#${n}`);
    const unnamed = p.orderNumbers.length - named.length;
    const what = [
      named.length ? `${named.length === 1 ? 'order' : 'orders'} ${listSome(named)}` : '',
      unnamed ? `${count(unnamed, 'AIB row')} without an order number` : '',
    ].filter(Boolean).join(' and ');
    problems.push(`Payment #${p.paymentId} (${p.amount.toFixed(2)} ${p.currency}) is matched, but ${what} could not be linked. `
      + 'Run Sync to fetch new orders. The payment is updated when its orders are linked.');
  }
  if (data.ordersNotFound > 0) {
    problems.push(`The order was not found for ${count(data.ordersNotFound, 'AIB row')}. Run Sync to fetch new orders.`);
  }
  if ((data.ordersCurrencyMismatch || 0) > 0) {
    problems.push(`${count(data.ordersCurrencyMismatch!, 'AIB row')} not linked, because the order has a different currency.`);
  }
  if ((data.rowsWithCurrencyMismatch || 0) > 0) {
    problems.push(`${count(data.rowsWithCurrencyMismatch!, 'AIB row')} not linked, because the transaction currency differs from the settlement currency.`);
  }
  problems.push(...linkedToOtherPaymentsProblems(data.linkedToOtherPayments, data.ordersConflictingLink || 0));
  if ((data.orderTotalMismatches || 0) > 0) {
    problems.push(`The order total differs from the AIB amount for ${count(data.orderTotalMismatches!, 'order')}.`);
  }
  if ((data.rowsUnusable || 0) > 0) {
    problems.push(`${count(data.rowsUnusable!, 'AIB adjustment row')} skipped, because the row has no date, currency or amount.`);
  }
  return problems;
}

/**
 * The result message of a Braintree AIB import. `countsPrefix` goes in front
 * of the counts (the overwrite run puts its wipe counts there).
 */
export function formatBraintreeReconcileResult(data: BraintreeReconcileResult, countsPrefix: string[] = []): ResultMessage {
  const parts: string[] = [
    ...countsPrefix,
    `${count(data.ordersLinked, 'order')} linked`,
    `${count(data.paymentsMatched, 'payment')} matched`,
  ];
  if (data.ordersAlreadyLinked > 0) parts.push(`${data.ordersAlreadyLinked} already linked`);
  // unclaimedRows also counts old rows from earlier imports; only this report's rows matter here.
  const reportRowsWaiting = data.reportRowsUnclaimed ?? data.unclaimedRows;
  if (reportRowsWaiting > 0) parts.push(`${count(reportRowsWaiting, 'row')} from this report wait for a later payout`);
  if ((data.refundsParsed || 0) > 0) parts.push(`${count(data.refundsParsed!, 'refund')} recorded`);
  if ((data.feeOnlyParsed || 0) > 0) parts.push(`${count(data.feeOnlyParsed!, 'AIB adjustment')} (${(data.feeOnlyNet ?? 0).toFixed(2)})`);
  return buildResultMessage(`Braintree AIB: ${parts.join(' · ')}`, braintreeReconcileProblems(data));
}

// ─── PayPal ───────────────────────────────────────────────────────────────────

export function formatPaypalReconcileResult(data: PaypalReconcileResult): ResultMessage {
  const parts: string[] = [];
  const problems: string[] = [];
  for (const [currency, cs] of Object.entries(data.currencies)) {
    parts.push(`${currency} ${cs.totalNet.toFixed(2)} net (${count(cs.ordersLinked, 'order')}, ${cs.totalFees.toFixed(2)} fees)`);
    if (cs.refundsApplied > 0) parts.push(`${count(cs.refundsApplied, 'refund')}`);
    if (cs.ordersNotFound > 0) {
      problems.push(`${currency}: ${count(cs.ordersNotFound, 'PayPal transaction')} not linked, because the order number is missing or the order is not in the database. Run Sync to fetch new orders.`);
    }
    if ((cs.ordersCurrencyMismatch || 0) > 0) {
      problems.push(`${currency}: ${count(cs.ordersCurrencyMismatch!, 'PayPal transaction')} not linked, because the order has a different currency.`);
    }
    problems.push(...linkedToOtherPaymentsProblems(cs.linkedToOtherPayments, cs.conflictingLinks || 0)
      .map((line) => `${currency}: ${line}`));
  }
  if (data.skipped > 0) parts.push(`${data.skipped} already reconciled`);
  if (data.relevantTransactions === 0 && data.skipped === 0) {
    problems.push('The file has no PayPal order payments or refunds. Check that it is the right PayPal report.');
  }
  return buildResultMessage(`PayPal: ${parts.length ? parts.join(' · ') : 'nothing imported'}`, problems);
}

// ─── Vasco ────────────────────────────────────────────────────────────────────

export function formatVascoReconcileResult(data: VascoReconcileResult): ResultMessage {
  const parts: string[] = [
    `${count(data.invoicesStored, 'invoice')} imported`,
    `${count(data.ordersUpdated, 'order')} updated`,
  ];
  if (data.vascoLinksAutoCreated) parts.push(`${count(data.vascoLinksAutoCreated, 'Google/Apple payment')} linked`);
  if (data.creditNotesStored > 0) parts.push(count(data.creditNotesStored, 'credit note'));
  const problems: string[] = [];
  if (data.invoicesFailed > 0) {
    problems.push(`${count(data.invoicesFailed, 'invoice')} could not be stored. See the server log.`);
  }
  if (data.ordersNotFound > 0) {
    problems.push(`${count(data.ordersNotFound, 'order number')} from Vasco not found in the database. Run Sync to fetch new orders, then import the file again.`);
  }
  if (data.skippedBlank > 0) {
    problems.push(`${count(data.skippedBlank, 'row')} skipped, because the invoice number or year is blank.`);
  }
  if ((data.vascoLinksAmbiguous || 0) > 0) {
    problems.push(`${count(data.vascoLinksAmbiguous!, 'Google/Apple payment')} not linked, because more than one Vasco invoice has the same amount. Link ${itOrThem(data.vascoLinksAmbiguous!)} by hand.`);
  }
  return buildResultMessage(`Vasco: ${parts.join(' · ')}`, problems);
}

// ─── Bank payment import (XML statement and Revolut API) ─────────────────────

/** A pending PayPal payout that matched more than one bank payment, so nothing was merged. */
export function paypalSettlementProblems(checks: PaypalSettlementCheck[] | undefined): string[] {
  return (checks ?? [])
    .filter((c) => c.ambiguous)
    .map((c) => `The PayPal payout of ${Number(c.expectedAmount).toFixed(2)} ${c.currency} (pending payment #${c.stubId}) `
      + `matches more than one bank payment (${(c.candidatePaymentIds ?? []).map((id) => `#${id}`).join(', ')}). `
      + 'It was not merged. Check these payments by hand.');
}

// ─── Bank transfers ───────────────────────────────────────────────────────────

export function formatBankTransferMatchResult(data: MatchResult): ResultMessage {
  const parts: string[] = [
    `Matched ${count(data.matched, 'payment')} (${count(data.ordersLinked, 'order')} linked)`,
  ];
  if (data.alreadyLinked > 0) parts.push(`${data.alreadyLinked} already linked`);
  const problems: string[] = [];
  if (data.skipped > 0) {
    const ids = (data.skippedPaymentIds ?? []).map((id) => `#${id}`);
    problems.push(`${count(data.skipped, 'bank transfer')} found no unique order${ids.length ? ` (payments ${listSome(ids, 10)})` : ''}. `
      + `Link ${itOrThem(data.skipped)} by hand.`);
  }
  problems.push(...paymentDiscrepancyProblems(data.paymentsWithDiscrepancy));
  if ((data.conflictingLinks || 0) > 0) {
    problems.push(`${count(data.conflictingLinks!, 'order')} already linked to another payment.`);
  }
  return buildResultMessage(parts.join(' · '), problems);
}
